import React, { useEffect, useState } from "react";
import { PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { api } from "@/lib/api";
import { CP, CapCard, Pill, UserSearch, InitialAvatar } from "./ui";

const PROF_COLORS = { expert: "#10B981", proficient: "#2563EB", practitioner: "#F59E0B", learning: "#94A3B8" };
const PROF_LABEL = { expert: "Expert", proficient: "Advanced", practitioner: "Intermediate", learning: "Beginner" };

export default function PeopleProfile() {
  const [profile, setProfile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = (u) => {
    setBusy(true);
    api.get(`/capability/profile/${u.user_id}`)
      .then(({ data }) => setProfile(data))
      .catch(() => {})
      .finally(() => setBusy(false));
  };

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => data?.user_id && load({ user_id: data.user_id })).catch(() => {});
  }, []);

  const p = profile;
  const distData = p ? Object.entries(
    (p.inferred_skills || []).reduce((acc, s) => { acc[s.proficiency] = (acc[s.proficiency] || 0) + 1; return acc; }, {})
  ).map(([k, v]) => ({ name: PROF_LABEL[k] || k, value: v, key: k })) : [];

  return (
    <div className="space-y-4" data-testid="people-profile">
      <div className="max-w-md"><UserSearch onSelect={load} placeholder="Search an employee…" testid="profile-user-search" /></div>

      {busy && <div className="text-sm py-8 text-center" style={{ color: CP.sub }}>Loading profile…</div>}

      {p && !busy && (
        <>
          <div className="rounded-xl bg-white shadow-sm p-5 flex flex-col md:flex-row md:items-center gap-5" style={{ border: `1px solid ${CP.border}` }} data-testid="profile-header">
            <div className="flex items-center gap-4">
              <InitialAvatar name={p.user?.name} size={64} />
              <div>
                <div className="text-lg font-bold" style={{ color: CP.text }}>{p.user?.name}</div>
                <div className="text-sm" style={{ color: CP.sub }}>{p.user?.title || p.user?.role}</div>
                <div className="mt-1"><Pill tone="blue">{p.user?.role}</Pill></div>
              </div>
            </div>
            <div className="md:ml-auto grid grid-cols-2 sm:grid-cols-4 gap-4">
              <HeadStat label="Total Credits" value={(p.wallet?.lifetime_earned || 0).toLocaleString()} />
              <HeadStat label="Reputation Score" value={`${(Math.min(5, (p.scores?.quality || 0) / 20)).toFixed(1)} / 5`} />
              <HeadStat label="Active Tasks" value={p.active_tasks} />
              <HeadStat label="Reviews" value={p.reviews_count} />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <CapCard title="Top Skills" testid="profile-top-skills">
              <div className="space-y-3">
                {(p.inferred_skills || []).slice(0, 6).map((s) => (
                  <div key={s.skill}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="capitalize font-medium" style={{ color: CP.text }}>{s.skill}</span>
                      <span className="font-semibold" style={{ color: PROF_COLORS[s.proficiency] }}>
                        {(s.confidence * 5).toFixed(1)} · {PROF_LABEL[s.proficiency]}
                      </span>
                    </div>
                    <div className="h-2 rounded-full" style={{ background: "#EDF1F9" }}>
                      <div className="h-full rounded-full" style={{ width: `${s.confidence * 100}%`, background: PROF_COLORS[s.proficiency] }} />
                    </div>
                  </div>
                ))}
                {(p.inferred_skills || []).length === 0 && <div className="text-xs" style={{ color: CP.sub }}>No evidence-backed skills yet.</div>}
              </div>
            </CapCard>

            <CapCard title="Skills Distribution" testid="profile-skill-dist">
              <div className="relative flex justify-center">
                <PieChart width={190} height={190}>
                  <Pie data={distData} dataKey="value" innerRadius={55} outerRadius={80} paddingAngle={2} stroke="none">
                    {distData.map((d) => <Cell key={d.key} fill={PROF_COLORS[d.key]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                </PieChart>
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <div className="text-2xl font-bold" style={{ color: CP.text }}>{(p.inferred_skills || []).length}</div>
                  <div className="text-[10px]" style={{ color: CP.sub }}>Total Skills</div>
                </div>
              </div>
              <div className="flex flex-wrap justify-center gap-3 mt-1 text-[11px]" style={{ color: CP.sub }}>
                {distData.map((d) => (
                  <span key={d.key} className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full inline-block" style={{ background: PROF_COLORS[d.key] }} />
                    {d.name} ({d.value})
                  </span>
                ))}
              </div>
            </CapCard>

            <CapCard title="Skills Over Time" subtitle="Cumulative distinct skills evidenced" testid="profile-skill-trend">
              <div style={{ width: "100%", height: 190 }}>
                <ResponsiveContainer>
                  <LineChart data={p.skills_over_time || []}>
                    <XAxis dataKey="month" tick={{ fontSize: 9, fill: CP.sub }} tickFormatter={(m) => m.slice(5)} />
                    <YAxis tick={{ fontSize: 9, fill: CP.sub }} width={24} />
                    <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                    <Line type="monotone" dataKey="skills" stroke={CP.purple} strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CapCard>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <FootStat label="Tasks Completed" value={p.completed_tasks} color={CP.blue} />
            <FootStat label="Credits Earned" value={`+${(p.wallet?.lifetime_earned || 0).toLocaleString()}`} color={CP.green} />
            <FootStat label="Success Rate" value={`${Math.min(100, Math.round(p.scores?.quality || 0))}%`} color={CP.purple} />
            <FootStat label="Avg Rating" value={`${(Math.min(5, (p.scores?.quality || 0) / 20)).toFixed(1)} / 5`} color={CP.amber} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <CapCard title="Recent Evidence" testid="profile-evidence">
              <div className="space-y-2">
                {(p.recent_evidence || []).slice(0, 5).map((e) => (
                  <div key={e.task_id} className="flex items-center justify-between text-xs py-1.5" style={{ borderBottom: `1px solid ${CP.border}` }}>
                    <span className="truncate mr-2" style={{ color: CP.text }}>{e.title}</span>
                    <span className="font-semibold shrink-0" style={{ color: CP.green }}>+{e.credits} cr</span>
                  </div>
                ))}
                {(p.recent_evidence || []).length === 0 && <div className="text-xs" style={{ color: CP.sub }}>No completed work yet.</div>}
              </div>
            </CapCard>
            <CapCard title="Frequent Collaborators" testid="profile-collabs">
              <div className="flex flex-wrap gap-2">
                {(p.collaborators || []).slice(0, 8).map((c) => (
                  <div key={c.user_id} className="flex items-center gap-2 rounded-full pl-1 pr-3 py-1" style={{ border: `1px solid ${CP.border}` }}>
                    <InitialAvatar name={c.name} size={24} />
                    <span className="text-xs" style={{ color: CP.text }}>{c.name}</span>
                  </div>
                ))}
                {(p.collaborators || []).length === 0 && <div className="text-xs" style={{ color: CP.sub }}>No collaborators yet.</div>}
              </div>
            </CapCard>
          </div>
        </>
      )}
    </div>
  );
}

function HeadStat({ label, value }) {
  return (
    <div>
      <div className="text-xl font-bold" style={{ color: CP.navy }}>{value}</div>
      <div className="text-[11px]" style={{ color: CP.sub }}>{label}</div>
    </div>
  );
}
function FootStat({ label, value, color }) {
  return (
    <div className="rounded-xl bg-white shadow-sm p-4" style={{ border: `1px solid ${CP.border}` }}>
      <div className="text-xl font-bold" style={{ color }}>{value}</div>
      <div className="text-[11px]" style={{ color: CP.sub }}>{label}</div>
    </div>
  );
}
